import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Header from '@/components/feature/Header';
import FooterSection from '@/pages/home/components/FooterSection';
import SchemaOrg from '@/components/base/SchemaOrg';
import { buildArticleSchema, buildFAQPageSchema } from '@/mocks/schema-data';
import { articleContents, type ArticleContent } from '@/mocks/article-contents';

interface ArticleTemplateProps {
  slug: string;
}

export default function ArticleTemplate({ slug }: ArticleTemplateProps) {
  const [activeFaq, setActiveFaq] = useState<number | null>(null);
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const toggleFaq = (index: number) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  const handleGoToMain = () => {
    window.open('https://kaspreadsheet.com', '_blank', 'noopener,nofollow');
  };

  const content: ArticleContent | undefined = articleContents[slug];
  const articleSchema = buildArticleSchema(slug);
  const faqSchema = buildFAQPageSchema(slug);

  if (!content) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl text-foreground-950 mb-4">Article Not Found</h1>
          <a href="/" className="text-primary-600 hover:text-primary-700">Return to Homepage</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <SchemaOrg data={articleSchema} />
      {faqSchema && <SchemaOrg data={faqSchema} />}
      <Header />

      {/* Article Hero */}
      <section className="relative w-full min-h-[380px] md:min-h-[460px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={
              slug === 'sugargoo-spreadsheet-review' ? 'https://readdy.ai/api/search-image?query=A%20clean%20modern%20desk%20with%20an%20open%20laptop%20showing%20a%20spreadsheet%20review%20interface%20a%20notebook%20with%20handwritten%20review%20notes%20and%20a%20cup%20of%20coffee%20soft%20morning%20light%20from%20a%20window%20cream%20beige%20aesthetic%20with%20sage%20green%20accents%20editorial%20product%20review%20photography%20minimal%20style%20warm%20tones%20calm%20ambiance&width=1600&height=800&seq=art-review-hero-v2&orientation=landscape' :
              slug === 'sugargoo-shipping-cost' ? 'https://readdy.ai/api/search-image?query=A%20flat%20lay%20of%20international%20shipping%20items%20including%20a%20cardboard%20box%20packing%20tape%20a%20shipping%20label%20a%20scale%20and%20a%20calculator%20on%20a%20cream%20colored%20desk%20warm%20natural%20light%20organized%20shipping%20supplies%20aesthetic%20clean%20minimal%20composition%20soft%20beige%20tones&width=1600&height=800&seq=art-shipping-hero-v2&orientation=landscape' :
              slug === 'sugargoo-coupon-guide' ? 'https://readdy.ai/api/search-image?query=A%20neatly%20arranged%20desk%20scene%20with%20discount%20tags%20coupon%20cards%20a%20smartphone%20displaying%20a%20discount%20code%20screen%20and%20a%20small%20shopping%20cart%20icon%20warm%20bright%20natural%20lighting%20cream%20beige%20and%20soft%20green%20palette%20clean%20minimalist%20aesthetic%20with%20gentle%20shadows&width=1600&height=800&seq=art-coupon-hero-v2&orientation=landscape' :
              slug === 'best-sugargoo-finds' ? 'https://readdy.ai/api/search-image?query=A%20curated%20flat%20lay%20of%20trendy%20fashion%20items%20including%20white%20sneakers%20a%20designer%20inspired%20hoodie%20a%20baseball%20cap%20sunglasses%20and%20a%20leather%20bag%20arranged%20on%20a%20cream%20linen%20fabric%20background%20soft%20warm%20lighting%20editorial%20product%20photography%20clean%20minimalist%20style%20with%20sage%20green%20accents&width=1600&height=800&seq=art-finds-hero-v2&orientation=landscape' :
              slug === 'is-sugargoo-legit' ? 'https://readdy.ai/api/search-image?query=A%20clean%20modern%20desk%20with%20a%20security%20shield%20icon%20on%20a%20laptop%20screen%20a%20checklist%20document%20with%20green%20checkmarks%20and%20a%20padlock%20symbol%20nearby%20soft%20warm%20daylight%20cream%20background%20professional%20trustworthy%20aesthetic%20minimal%20design%20with%20sage%20green%20details&width=1600&height=800&seq=art-legit-hero-v2&orientation=landscape' :
              slug === 'sugargoo-alternatives' ? 'https://readdy.ai/api/search-image?query=A%20comparison%20chart%20on%20a%20laptop%20screen%20showing%20multiple%20shopping%20agent%20logos%20side%20by%20side%20with%20rating%20bars%20and%20feature%20checkmarks%20a%20notebook%20with%20handwritten%20comparison%20notes%20on%20the%20desk%20soft%20warm%20lighting%20clean%20professional%20workspace%20cream%20beige%20aesthetic&width=1600&height=800&seq=art-alt-hero-v2&orientation=landscape' :
              slug === 'sugargoo-shoes-guide' ? 'https://readdy.ai/api/search-image?query=A%20beautiful%20flat%20lay%20of%20stylish%20sneakers%20including%20white%20leather%20trainers%20and%20retro%20running%20shoes%20on%20a%20cream%20colored%20surface%20with%20a%20laptop%20showing%20a%20spreadsheet%20in%20the%20background%20soft%20natural%20light%20editorial%20fashion%20photography%20clean%20minimalist%20aesthetic%20warm%20tones&width=1600&height=800&seq=art-shoes-hero-v2&orientation=landscape' :
              slug === 'sugargoo-fashion-guide' ? 'https://readdy.ai/api/search-image?query=A%20stylish%20flat%20lay%20of%20contemporary%20streetwear%20including%20a%20beige%20oversized%20hoodie%20loose%20fit%20denim%20jeans%20a%20canvas%20tote%20bag%20and%20minimalist%20jewelry%20on%20a%20cream%20linen%20background%20soft%20diffused%20natural%20light%20editorial%20fashion%20aesthetic%20warm%20neutral%20tones%20relaxed%20vibe&width=1600&height=800&seq=art-fashion-hero-v2&orientation=landscape' :
              'https://readdy.ai/api/search-image?query=A%20desk%20scene%20with%20a%20piggy%20bank%20a%20calculator%20stacked%20coins%20and%20a%20smartphone%20showing%20a%20shopping%20cart%20with%20savings%20notification%20soft%20warm%20daylight%20clean%20cream%20background%20financial%20planning%20aesthetic%20minimal%20composition%20with%20sage%20green%20plant&width=1600&height=800&seq=art-saving-hero-v2&orientation=landscape'
            }
            alt={content.h1}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/18 via-black/10 to-black/28"></div>
        </div>

        <div className="relative z-10 w-full max-w-3xl mx-auto px-4 md:px-6 text-center py-16">
          <span className="inline-block text-xs font-medium text-primary-300 bg-background-50/20 backdrop-blur-sm px-3 py-1 rounded-full mb-4 whitespace-nowrap">
            {content.tag}
          </span>
          <h1 className="text-2xl md:text-4xl lg:text-5xl text-background-50 mb-4 leading-tight">
            {content.h1}
          </h1>
          <p className="text-background-50/80 text-sm md:text-base max-w-lg mx-auto">
            {content.subtitle}
          </p>
        </div>
      </section>

      {/* Article Content */}
      <article className="py-12 md:py-16 px-4 md:px-6">
        <div className="max-w-3xl mx-auto">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-8 flex-wrap">
            <a href="/" className="hover:text-primary-600 transition-colors duration-200 whitespace-nowrap">Home</a>
            <span className="w-3 h-3 flex items-center justify-center">
              <i className="ri-arrow-right-s-line text-[10px]"></i>
            </span>
            <span className="text-foreground-400 whitespace-nowrap">{content.breadcrumb}</span>
          </nav>

          {/* Intro */}
          <div className="prose-custom mb-10">
            {content.intro.map((p, i) => (
              <p
                key={i}
                className="text-foreground-700 text-sm md:text-base leading-relaxed mb-4"
                dangerouslySetInnerHTML={{ __html: p }}
              ></p>
            ))}
          </div>

          {/* Table of Contents */}
          <div className="bg-background-100/60 border border-background-200/70 rounded-lg p-5 md:p-6 mb-10">
            <h3 className="font-heading text-lg text-foreground-950 mb-4">Table of Contents</h3>
            <ul className="space-y-2">
              {content.toc.map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-primary-500 text-xs font-medium mt-0.5 whitespace-nowrap">{String(i + 1).padStart(2, '0')}.</span>
                  <span className="text-sm text-foreground-600">{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Sections */}
          {content.sections.map((section, si) => (
            <section key={si} className="mb-10">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-4">
                {section.heading}
              </h2>

              {section.paragraphs.length > 0 && section.paragraphs.map((p, pi) => (
                <p
                  key={pi}
                  className="text-foreground-700 text-sm md:text-base leading-relaxed mb-4"
                  dangerouslySetInnerHTML={{ __html: p }}
                ></p>
              ))}

              {section.type === 'list' && section.items && (
                <ul className="space-y-3 mb-4">
                  {section.items.map((item) => (
                    <li key={item.title} className="flex items-start gap-3">
                      <span className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0 rounded-full bg-primary-100 text-primary-600">
                        <i className="ri-check-line text-xs"></i>
                      </span>
                      <div>
                        <strong className="text-sm text-foreground-800">{item.title}: </strong>
                        <span className="text-sm text-foreground-600">{item.desc}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              {section.type === 'tips' && section.items && (
                <div className="space-y-4 mb-4">
                  {section.items.map((item, ti) => (
                    <div key={ti} className="flex items-start gap-4 p-4 bg-background-100/60 rounded-lg">
                      <span className="w-8 h-8 flex items-center justify-center flex-shrink-0 rounded-full bg-primary-500 text-background-50 text-sm font-medium">
                        {ti + 1}
                      </span>
                      <div>
                        <h4 className="text-sm font-medium text-foreground-800 mb-1">{item.title}</h4>
                        <p className="text-xs md:text-sm text-foreground-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: item.desc }}></p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {section.type === 'comparison' && section.tableRows && (
                <div className="overflow-x-auto mb-4">
                  <table className="w-full border-collapse text-sm">
                    <thead>
                      <tr className="border-b-2 border-background-200">
                        <th className="text-left py-3 px-3 font-medium text-foreground-800 whitespace-nowrap">Aspect</th>
                        <th className="text-left py-3 px-3 font-medium text-primary-700 whitespace-nowrap">Sugargoo Spreadsheet</th>
                        <th className="text-left py-3 px-3 font-medium text-foreground-500 whitespace-nowrap">Alternative / Risk</th>
                      </tr>
                    </thead>
                    <tbody>
                      {section.tableRows.map((row, ri) => (
                        <tr key={ri} className={`border-b border-background-100 ${ri % 2 === 0 ? 'bg-background-50' : 'bg-background-100/40'}`}>
                          <td className="py-3 px-3 text-foreground-700 font-medium whitespace-nowrap">{row.aspect}</td>
                          <td className="py-3 px-3 text-primary-700 whitespace-nowrap">{row.col1}</td>
                          <td className="py-3 px-3 text-foreground-500 whitespace-nowrap">{row.col2}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          ))}

          {/* FAQ */}
          {content.faqs.length > 0 && (
            <section className="mb-10">
              <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-6">
                Frequently Asked Questions
              </h2>
              <div className="space-y-3">
                {content.faqs.map((faq, fi) => (
                  <div key={fi} className="border border-background-200/70 rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleFaq(fi)}
                      className="w-full flex items-center justify-between p-4 text-left cursor-pointer hover:bg-background-100/40 transition-colors duration-200"
                    >
                      <span className="text-sm font-medium text-foreground-800 pr-4">{faq.q}</span>
                      <span className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                        {activeFaq === fi ? (
                          <i className="ri-subtract-line text-foreground-400"></i>
                        ) : (
                          <i className="ri-add-line text-foreground-400"></i>
                        )}
                      </span>
                    </button>
                    {activeFaq === fi && (
                      <div className="px-4 pb-4">
                        <p className="text-sm text-foreground-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: faq.a }}></p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* CTA */}
          <section className="mb-10">
            <div className="bg-primary-50/50 border border-primary-200/50 rounded-lg p-5 md:p-6">
              <h3 className="font-heading text-lg text-primary-800 mb-3">{content.ctaTitle}</h3>
              <p className="text-sm text-primary-700 mb-4 leading-relaxed">{content.ctaText}</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="/"
                  className="inline-flex items-center justify-center gap-2 bg-primary-500 text-background-50 px-6 py-2.5 rounded-md text-sm font-medium hover:bg-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                >
                  Back to Homepage
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line"></i>
                  </span>
                </a>
                <button
                  onClick={handleGoToMain}
                  className="inline-flex items-center justify-center gap-2 border border-primary-300 text-primary-700 px-6 py-2.5 rounded-md text-sm font-medium hover:bg-primary-100 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                >
                  Visit Main Website
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-external-link-line"></i>
                  </span>
                </button>
              </div>
            </div>
          </section>
        </div>
      </article>

      <FooterSection />
    </div>
  );
}